import React, { useEffect, useState } from "react";
import DataTable, { SortOrder } from "react-data-table-component";
import { withTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { Form, Input } from "reactstrap";
import { Loader } from "src/components";
import { getPaginationRequestInitialValue, PaginationRequest } from "src/sdk";
import { OrganisationType, TicketFieldSetting } from "src/sdk/models/Organisation";
import { Ticket } from "src/sdk/models/Ticket";
import { OrganisationService } from "src/services/OrganisationService";
import { TicketService } from "src/services/TicketService";
import { TicketingColumns } from "./TicketingColumns";
import TicketingDetails from "./TicketingDetails";
import TicketingDetailsByTicketId from "./TicketingDetailsByTicketId";
import { StatusFilters } from "./Filter";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const service = new TicketService();
const serviceOrg = new OrganisationService()

export const Ticketing: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const [initialLoading, setInitialLoading] = useState(true);

  const [ticketsData, setTicketsData] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fields, setFields] = useState<TicketFieldSetting[]>([]);

  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [modal_xlarge, setmodal_xlarge] = useState(false);
  let query = useQuery();
  let ticketId = query.get("ticketId") as string;
  const [modalByTicketIdOpen, setModalByTicketIdOpen] = useState(false);

  const [paginationResetDefaultPage] = useState(false);
  const [totalRows, setTotalRows] = useState(10000);
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("ticketId");
  const [sortOrder, setSortOrder] = useState<string>("DESC");

  const STATUS_OPTIONS = [
    { key: "New", label: "New", btnClass: "btn btn-primary", default: true },
    { key: "Scheduled", label: "Scheduled", btnClass: "btn btn-primary", default: true },
    { key: "Escalated", label: "Escalated", btnClass: "btn btn-primary", default: true },
    { key: "Pending", label: "Pending", btnClass: "btn btn-primary", default: false },
    { key: "In-Progress", label: "In-Progress", btnClass: "btn btn-danger", default: false },
    { key: "Canceled", label: "Canceled", btnClass: "btn btn-primary", default: false },
    { key: "Email Ticket", label: "Email Ticket", btnClass: "btn btn-primary", default: false },
    { key: "Blocked", label: "Blocked", btnClass: "btn btn-primary", default: false }
  ];

  const [selectedStatuses, setSelectedStatuses] = React.useState<string[]>(
    STATUS_OPTIONS.filter(s => s.default).map(s => s.key)
  );

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    console.log(selectedStatuses);
  };

  const tog_xlarge = () => setmodal_xlarge(!modal_xlarge);

  useEffect(() => {
    if (!location.state) {
      serviceOrg.getOrganisationsByUser().then((res) => {
        const firstOrg = res.data?.[0];
        if (firstOrg) {
          history.replace({
            pathname: "/ticketing",
            search: location.search,
            state: firstOrg
          });
        }
        setInitialLoading(false);
      });
    } else {
      setInitialLoading(false);
    }
  }, []);


  const fetchPage = async (page: number, search: string, sortBy: string, sortOrder: string) => {
    if (!location.state) return;

    try {
      setLoading(true);

      const pagination = getPaginationRequestInitialValue();
      pagination.page = page;
      pagination.pageSize = perPage;
      pagination.search = search;
      pagination.sortBy = sortBy;
      pagination.sortOrder = sortOrder;
      pagination.filemakerUUID = location.state?.filemakerUUID ?? "";
      pagination.statuses = selectedStatuses;

      setFields([]);
      setTicketsData([]);

      const loadedFields = await getTicketFields(setFields, setLoading, location.state?.id ?? 0, location.state?.type ?? '');
      await getTickets(loadedFields, pagination, setTicketsData, setTotalRows);
    } catch (error) {
      console.error("API ERROR in fetchPage:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialLoading) return;
    fetchPage(page, search, sortBy, sortOrder);
  }, [page, perPage, search, initialLoading, location.state, selectedStatuses]);

  useEffect(() => {
    if (ticketId) {
      setModalByTicketIdOpen(true);
    }
  }, [ticketId]);

  const handleDetailsClick = (ticket: Ticket) => {
    setSelectedTicket(null);
    setSelectedTicket(ticket);
    setmodal_xlarge(true);
  };


  const handlePageChange = (pageNew: number) => {
    setPage(pageNew);
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const sortPagination = (column: any, direction: SortOrder) => {
    setSortOrder(direction);
    setSortBy(column.id);
    fetchPage(page, search, column.id, direction);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  if (initialLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center mt-5">
        <Loader loading={true} />
      </div>
    );
  }

  if (!location.state?.filemakerUUID) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <h3>FilemakerUUID missing!</h3>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="container-fluid mt-4">
        <div className="row">
          <div className="col-12">
            <div style={{ paddingTop: 10, paddingBottom: 20, display: "flex", flexDirection: "row" }}>
              <h5 style={{ marginRight: 13 }}>{location.state.name}</h5>
              <i className="bi bi-chevron-right"></i>
              <h5 style={{ marginLeft: 13, opacity: 0.6 }}> Tickets</h5>
            </div>
          </div>
        </div>
        <div className="d-flex mb-3">
          <div className="me-auto w-50">
            <Form onSubmit={handleSearchSubmit}>
              <div className="input-group">
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button className="btn btn-secondary bi bi-search" type="submit" />
              </div>
            </Form>
          </div>

          <button className="btn btn-secondary" onClick={() =>
            history.push({
              pathname: '/create-ticket',
              state: { fields: fields, organisation: location.state }
            })
          }>
            Add Ticket
          </button>
        </div>

        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-body">
                <div className="row mb-2">
                  <div className="col-12">
                    <div
                      className="d-flex align-items-center"
                      style={{
                        fontSize: "12px",
                        color: "#6c757d", // bootstrap muted text
                      }}
                    >
                      <i
                        className="bi bi-exclamation-triangle me-2"
                        style={{ fontSize: "14px" }}
                      />
                      <span>
                        <strong>PHI Notice:</strong> Please do not enter, upload, or share any
                        Protected Health Information (PHI) in this application.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-3">
                  <div className="btn-group" role="group"  style={{ width: '100%' }}>
                    <StatusFilters
                      counts={{
                        "New": 8,
                        "Scheduled": 0,
                        "Escalated": 1,
                        "Pending": 0,
                        "In-Progress": 0,
                        "Canceled": 0,
                        "Email Ticket": 1,
                        "Blocked": 0,
                      }}
                      onChange={(statuses) => {
                        setSelectedStatuses(statuses);
                        console.log("selected statuses", statuses);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <DataTable
            data={ticketsData}
            columns={TicketingColumns(fields, handleDetailsClick)}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<Loader loading={loading} />}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={perPage}    
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            paginationRowsPerPageOptions={[50, 100]}
            onSort={sortPagination}
            defaultSortAsc={false}
            highlightOnHover
            striped
            responsive
          />
        </div>
      </div>

      {selectedTicket && (
        <TicketingDetails
          id={selectedTicket.id as number}
          modal_xlarge={modal_xlarge}
          setmodal_xlarge={setmodal_xlarge}
          tog_xlarge={tog_xlarge}
          fields={fields}
        />
      )}

      {ticketId && modalByTicketIdOpen && (
        <TicketingDetailsByTicketId
          ticketId={ticketId}
          modal_xlarge={modalByTicketIdOpen}
          setmodal_xlarge={setModalByTicketIdOpen}
          tog_xlarge={() => setModalByTicketIdOpen(!modalByTicketIdOpen)}
          fields={fields}
        />
      )}
    </React.Fragment>
  );
};

export default withTranslation()(Ticketing);

export const getTicketFields = async (
  setFields: (fields: TicketFieldSetting[]) => void,
  setLoading?: (loading: boolean) => void,
  id?: string,
  orgType?: string,
) => {
  try {
    setLoading?.(true);

    const response = await service.getFieldSettingsHeader(id as string);
    let fields = response.data;

    fields.sort((a: any, b: any) => (a.orderNo ?? 0) - (b.orderNo ?? 0));

    if (orgType === OrganisationType.Facility) {
      console.log("F");
      fields = fields.filter((f: TicketFieldSetting) => f.filedKey !== "facilityGroupName");
    } else if (orgType === OrganisationType.FacilityGroup) {
      fields = fields.filter((f: TicketFieldSetting) => f.filedKey !== "facilityName");
      console.log("FG");
    }

    setFields(fields);
    return fields;
  } catch (error) {
    console.error("API ERROR (getTicketFields):", error);
    return [];
  } finally {
    setLoading?.(false);
  }
};


export const getTickets = async (
  fields: TicketFieldSetting[],
  pagination: PaginationRequest,
  setTicketsData: any,
  setTotalRows: any,
  setLoading?: any
) => {
  try {
    setLoading?.(true);

    const response = await service.getTickets(pagination);
    setTotalRows(response.data.totalRecords);

    const rows = response.data.data.map((ticket: any) => {
      const obj: any = {};

      fields.forEach(f => {
        obj[f.name] = ticket[f.name];
      });

      return obj;
    });

    setTicketsData(rows);

  } catch (error) {
    console.error("API ERROR (getTickets):", error);
  } finally {
    setLoading?.(false);
  }
};

const customStyles = {
  headRow: {
    style: {
      fontSize: 12,
      height: "40px",
      minHeight: "40px",
    },
  },
  headCells: {
    style: {
      width: "10px",
      borderLeft: `1px solid #dee2e6`,
      borderRight: `1px solid #dee2e6`,
      backgroundColor: "#274755",
      color: "#FFFFFF",
      height: "40px",
      maxHeight: "40px",
      fontWeight: 600,
    },
  },
  rows: {
    style: {
      borderBottomWidth: "0px !important",
      minHeight: "50px",
    },
  },
  cells: {
    style: {
      width: "10px",
      height: "50px",
      fontSize: 12,
      border: "1px solid #dee2e6",
      fontWeight: "bold",
      paddingLeft: "8px",
      paddingRight: "8px",
    },
  },
  pagination: {
    style: {
      fontSize: 13,
      minHeight: "56px",
      borderTop: "1px solid #dee2e6",
      paddingTop: "8px",
    },
  },
};
